// Generated from scripts/cart-line-variant.ts. Rebuild with pnpm scripts:build.
import { recordAddedVariants, replaceLineInOrder } from '@theme/cart-line-order';
import { CartItemsComponent, changeCartLine } from '@theme/component-cart-items';
import { addLineFor, addToCart, propertiesOf, readCart, sellingPlanOf, withTimeout } from '@theme/product-form';
/**
 * Change variant: the control of a cart line that swaps its size or color without taking the line out of the cart.
 *
 * The control is a button and a popover (anchored-popover-component, positioned by styles/wontio.css). When the
 * popover first opens for a product, the product's variants are read from the product endpoint
 * (`<root>products/<handle>.js`, at most 250 variants) and kept for the page's life; the popover then shows one
 * select per product option. A value no available variant has is disabled and says why. Choosing a value keeps the
 * other selections when a variant allows it and moves them to the closest available variant when not, so no choice
 * leads to a combination that cannot be bought.
 *
 * Confirming is two writes through the cart owners, in this order, so the shopper never loses the line if the second
 * fails: `addToCart` adds the chosen variant with the quantity, line item properties and selling plan the line has in
 * the cart (read from the cart at that moment, not from the page), then `changeCartLine` sets the line it replaces to
 * 0 by its key. Both run in the cart list's queue and the list does not render between them, so the shopper sees the
 * line replaced once, in the place the old line held (the order memory is rewritten for it).
 *
 * Outcomes. A refused add (a 422) changes nothing and says why on the line. A failed removal leaves both lines in the
 * cart: the list renders what the cart holds and the old line says the original could not be removed. A lost answer
 * is settled by the owners' reading of the cart; an add the cart shows did not happen offers to try again, and one
 * the cart cannot show is left to the shopper to check, with the list rendered from the server.
 */
/** How long the product endpoint may take before the popover says the options did not load. */
const READ_TIMEOUT = 8000;
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function isCartItems(element) {
    return element instanceof CartItemsComponent;
}
function isToggle(event) {
    return 'newState' in event && typeof event.newState === 'string';
}
/** The option names of the product: the endpoint lists them as objects with a name. */
function optionNames(value) {
    if (!Array.isArray(value))
        return undefined;
    const names = [];
    for (const option of value) {
        const name = typeof option === 'string' ? option : isRecord(option) && typeof option.name === 'string' ? option.name : undefined;
        if (name === undefined)
            return undefined;
        names.push(name);
    }
    return names;
}
function variantOf(value, optionCount) {
    if (!isRecord(value) || typeof value.id !== 'number' || typeof value.title !== 'string' || typeof value.available !== 'boolean')
        return undefined;
    const options = [];
    for (const field of [value.option1, value.option2, value.option3].slice(0, optionCount)) {
        if (typeof field !== 'string')
            return undefined;
        options.push(field);
    }
    const allocations = value.selling_plan_allocations;
    const sellingPlans = Array.isArray(allocations)
        ? allocations.flatMap((allocation) => (isRecord(allocation) && typeof allocation.selling_plan_id === 'number' ? [String(allocation.selling_plan_id)] : []))
        : [];
    return { id: value.id, title: value.title, options, available: value.available, sellingPlans };
}
/** The part of the product endpoint's answer the popover reads, or undefined when it does not look like one. */
function parseProduct(data) {
    if (!isRecord(data))
        return undefined;
    const names = optionNames(data.options);
    if (!names || names.length === 0 || names.length > 3 || !Array.isArray(data.variants))
        return undefined;
    const variants = [];
    for (const entry of data.variants) {
        const variant = variantOf(entry, names.length);
        if (!variant)
            return undefined;
        variants.push(variant);
    }
    return variants.length > 0 ? { names, variants } : undefined;
}
/** The store root for the shopper's market and language, with its trailing slash. */
function rootUrl() {
    const root = Theme.routes.root_url;
    return root.endsWith('/') ? root : `${root}/`;
}
/** Products read this page, by handle. A failed read is not kept, so the next opening asks again. */
const products = new Map();
function loadProduct(handle) {
    const cached = products.get(handle);
    if (cached)
        return cached;
    const request = (async () => {
        const timeout = withTimeout(READ_TIMEOUT);
        try {
            const response = await fetch(`${rootUrl()}products/${encodeURIComponent(handle)}.js`, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                signal: timeout.signal,
            });
            return response.ok ? parseProduct(await response.json()) : undefined;
        }
        catch {
            return undefined;
        }
        finally {
            timeout.stop();
        }
    })();
    products.set(handle, request);
    void request.then((product) => {
        if (!product)
            products.delete(handle);
    });
    return request;
}
function sameProperties(a, b) {
    const left = Object.entries(propertiesOf(a) ?? {});
    const right = propertiesOf(b) ?? {};
    return left.length === Object.keys(right).length && left.every(([name, value]) => right[name] === value);
}
/**
 * The line the swap starts from, in a cart read just now. The key is the line's identity, but it changes when the
 * line's properties or discounts do, so a line whose key is gone is found by its variant when only one has it.
 */
function lineToSwap(cart, key, variantId) {
    const byKey = cart.items.find((item) => item.key === key);
    if (byKey)
        return byKey;
    const byVariant = cart.items.filter((item) => item.variant_id === variantId);
    return byVariant.length === 1 ? byVariant[0] : undefined;
}
/** The line to remove once the new variant is in: the original, found again because adding can change keys. */
function lineToRemove(cart, original, replacementId) {
    return (cart.items.find((item) => item.key === original.key) ??
        cart.items.find((item) => item.variant_id === original.variant_id && item.variant_id !== replacementId && sellingPlanOf(item) === sellingPlanOf(original) && sameProperties(item, original)));
}
class CartLineVariant extends HTMLElement {
    #lifetime;
    #product;
    #choice = [];
    #busy = false;
    /** Set while the popover closes because the swap finished, so the focus is placed by the swap and not returned. */
    #closingAfterSwap = false;
    connectedCallback() {
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        const { signal } = lifetime;
        this.#popover?.addEventListener('toggle', this.#onToggle, { signal });
        this.#options?.addEventListener('change', this.#onChange, { signal });
        this.#options?.querySelector('[data-line-change-submit]')?.addEventListener('click', this.#onUpdate, { signal });
        this.#options?.querySelector('[data-line-change-cancel]')?.addEventListener('click', () => this.#close(true), { signal });
    }
    disconnectedCallback() {
        this.#lifetime?.abort();
        this.#lifetime = undefined;
    }
    get #popover() {
        return this.querySelector('[popover]');
    }
    /** The selects and buttons of the popover. Not a form: the cart's lines sit inside the cart form and forms do not nest. */
    get #options() {
        return this.querySelector('[data-line-change-options]');
    }
    get #trigger() {
        return this.querySelector('[data-line-change-trigger]');
    }
    get #items() {
        const items = this.closest('cart-items-component');
        return isCartItems(items) ? items : undefined;
    }
    /** The key the line had when the page rendered it. */
    get #lineKey() {
        return this.closest('[data-key]')?.dataset.key;
    }
    get #variantId() {
        return Number(this.dataset.variantId);
    }
    /** The plan the line is sold with, which the variant it moves to has to offer too. */
    get #sellingPlan() {
        return this.dataset.sellingPlan || undefined;
    }
    #text(name) {
        return this.#items?.dataset[`variant${name}Text`] ?? '';
    }
    #setStatus(text) {
        const status = this.querySelector('[data-line-change-status]');
        if (status)
            status.textContent = text;
    }
    /** Closes the popover. `restoreFocus` is false when the swap has placed the focus itself. */
    #close(restoreFocus) {
        const popover = this.#popover;
        if (!popover?.matches(':popover-open'))
            return;
        this.#closingAfterSwap = !restoreFocus;
        popover.hidePopover();
    }
    #onToggle = (event) => {
        if (!isToggle(event))
            return;
        if (event.newState === 'open') {
            void this.#opened();
            return;
        }
        // The popover returns focus to its button when it closes with focus inside; a browser that does not focus a button
        // on press (Safari) has nothing to return it to, so the button is focused here.
        const restore = !this.#closingAfterSwap;
        this.#closingAfterSwap = false;
        const active = document.activeElement;
        if (restore && (active === null || active === document.body || this.#popover?.contains(active)))
            this.#trigger?.focus({ preventScroll: true });
    };
    async #opened() {
        const handle = this.dataset.productHandle;
        if (!handle)
            return;
        if (!this.#product) {
            this.#clearFields();
            this.#setStatus(this.#text('Loading'));
            this.#options?.setAttribute('aria-busy', 'true');
            const product = await loadProduct(handle);
            this.#options?.removeAttribute('aria-busy');
            // The popover closed, or the line went, while the options loaded.
            if (!this.isConnected || !this.#popover?.matches(':popover-open'))
                return;
            // A product the endpoint lists only in part cannot offer a swap whose combinations are all known.
            if (!product || !product.variants.some((variant) => variant.id === this.#variantId)) {
                this.#setStatus(this.#text('LoadError'));
                return;
            }
            this.#product = product;
            this.#build(product);
        }
        const current = this.#product?.variants.find((variant) => variant.id === this.#variantId);
        if (!current)
            return;
        this.#choice = [...current.options];
        this.#setStatus('');
        this.#refresh();
        this.querySelector('select')?.focus({ preventScroll: true });
    }
    #clearFields() {
        this.querySelector('[data-line-change-fields]')?.replaceChildren();
    }
    /** Builds one labelled select per product option from the field template the server rendered. */
    #build(product) {
        const fields = this.querySelector('[data-line-change-fields]');
        const template = this.querySelector('template[data-line-change-field]');
        if (!fields || !(template instanceof HTMLTemplateElement))
            return;
        this.#clearFields();
        product.names.forEach((name, index) => {
            const field = document.importNode(template.content, true);
            const label = field.querySelector('label');
            const select = field.querySelector('select');
            if (!label || !select)
                return;
            select.id = `${this.id}-option-${index}`;
            select.dataset.optionIndex = String(index);
            label.htmlFor = select.id;
            label.textContent = name;
            for (const value of new Set(product.variants.map((variant) => variant.options[index]))) {
                if (value !== undefined)
                    select.append(Object.assign(document.createElement('option'), { value, textContent: value }));
            }
            fields.append(field);
        });
    }
    #eligible(variant) {
        const plan = this.#sellingPlan;
        return variant.available && (plan === undefined || variant.sellingPlans.includes(plan));
    }
    /** Whether any variant with `value` for the option at `index` can be chosen, and if not, why. */
    #stateOf(product, index, value) {
        const withValue = product.variants.filter((variant) => variant.options[index] === value);
        if (withValue.some((variant) => this.#eligible(variant)))
            return 'ok';
        return withValue.some((variant) => variant.available) ? 'unavailable' : 'sold-out';
    }
    /** The eligible variant with `value` at `index` that keeps the most of the other selections. */
    #closestVariant(product, index, value) {
        let best;
        let bestScore = -1;
        for (const variant of product.variants) {
            if (variant.options[index] !== value || !this.#eligible(variant))
                continue;
            const score = variant.options.filter((option, position) => position !== index && option === this.#choice[position]).length;
            if (score > bestScore) {
                best = variant;
                bestScore = score;
            }
        }
        return best;
    }
    get #chosen() {
        return this.#product?.variants.find((variant) => variant.options.every((option, index) => option === this.#choice[index]));
    }
    /** Shows the selection and what each value allows, and enables Update only for a different variant that can be bought. */
    #refresh() {
        const product = this.#product;
        if (!product)
            return;
        for (const select of this.querySelectorAll('select[data-option-index]')) {
            if (!(select instanceof HTMLSelectElement))
                continue;
            const index = Number(select.dataset.optionIndex);
            for (const option of select.options) {
                const state = this.#stateOf(product, index, option.value);
                option.disabled = state !== 'ok';
                option.textContent = state === 'ok' ? option.value : `${option.value} · ${this.#text(state === 'sold-out' ? 'SoldOut' : 'Unavailable')}`;
            }
            select.value = this.#choice[index] ?? '';
        }
        const chosen = this.#chosen;
        const submit = this.querySelector('[data-line-change-submit]');
        if (submit)
            submit.disabled = this.#busy || !chosen || chosen.id === this.#variantId || !this.#eligible(chosen);
    }
    #onChange = (event) => {
        const select = event.target;
        const product = this.#product;
        if (!product || !(select instanceof HTMLSelectElement) || select.dataset.optionIndex === undefined)
            return;
        const closest = this.#closestVariant(product, Number(select.dataset.optionIndex), select.value);
        if (closest)
            this.#choice = [...closest.options];
        this.#refresh();
    };
    #onUpdate = () => {
        const variant = this.#chosen;
        if (this.#busy || !variant || !this.#eligible(variant))
            return;
        this.#run(variant);
    };
    /** Runs the swap in the list's queue, with the popover busy, and closes the popover when it is done. */
    #run(variant) {
        const items = this.#items;
        if (!items || this.#busy)
            return;
        this.#setBusy(true);
        items
            .enqueue(() => this.#swap(items, variant))
            .then((swapped) => this.#close(!swapped))
            .catch((error) => {
            console.warn('[cart-line-variant] The swap failed:', error);
            this.#close(true);
        })
            .finally(() => this.#setBusy(false));
    }
    #setBusy(busy) {
        this.#busy = busy;
        this.#options?.toggleAttribute('aria-busy', busy);
        for (const control of this.querySelectorAll('select, [data-line-change-cancel]')) {
            if (control instanceof HTMLSelectElement || control instanceof HTMLButtonElement)
                control.disabled = busy;
        }
        this.#refresh();
    }
    /**
     * The swap, with the list holding its render so it draws the outcome once: the replacement in the old line's place
     * when both writes happened, or what the cart holds when one did not. Resolves whether the swap happened.
     */
    async #swap(items, variant) {
        const key = this.#lineKey;
        if (!key)
            return false;
        // Read before the list renders: the render replaces this line, and a control that has left the page reads nothing.
        const changed = this.#text('Changed').replace('[variant]', variant.title);
        const release = CartItemsComponent.holdRendering();
        let result;
        try {
            result = await this.#attempt(items, key, variant);
        }
        finally {
            await release();
        }
        if (result.swapped) {
            items.announce(changed);
            items.querySelector(`cart-line-variant[data-variant-id="${variant.id}"] [data-line-change-trigger]`)?.focus({ preventScroll: true });
            return true;
        }
        if (result.reconcile)
            await items.reconcile();
        if (!items.showLineMessage(key, result.message, result.retry ? { retry: result.retry } : {}))
            items.announce(result.message);
        return false;
    }
    /** The two writes. Everything the shopper is told about them is in the result. */
    async #attempt(items, key, variant) {
        const cart = await readCart();
        const original = cart ? lineToSwap(cart, key, this.#variantId) : undefined;
        if (!original)
            return { swapped: false, message: this.#text('Failed'), reconcile: true };
        const added = await addToCart({ source: this, context: 'cart', lines: [addLineFor(original, variant.id)] });
        switch (added.status) {
            case 'rejected':
                return { swapped: false, message: added.description || added.message || this.#text('Failed') };
            case 'not-added':
                // The cart shows nothing was added, so asking again cannot make a second line.
                return { swapped: false, message: this.#text('Failed'), retry: () => this.#run(variant) };
            case 'unknown':
                return { swapped: false, message: this.#text('Unconfirmed'), reconcile: true };
            case 'added':
                break;
            default: {
                const exhaustive = added;
                throw new Error(`Unhandled add outcome ${String(exhaustive)}`);
            }
        }
        // Adding can change the keys of other lines, so the line to remove is found again in the cart the add left.
        const after = added.cart ?? (await readCart());
        const remaining = after ? lineToRemove(after, original, variant.id) : undefined;
        if (after && !remaining) {
            // The original is gone already; the add is the whole swap.
            replaceLineInOrder(items, key, variant.id);
            return { swapped: true };
        }
        if (!remaining) {
            recordAddedVariants([variant.id]);
            return { swapped: false, message: this.#text('Partial'), reconcile: true };
        }
        const removed = await changeCartLine({ source: this, key: remaining.key, quantity: 0 });
        if (removed.status === 'changed') {
            replaceLineInOrder(items, key, variant.id);
            return { swapped: true };
        }
        // Both lines are in the cart. The list renders them, newest first, and the original says what happened.
        recordAddedVariants([variant.id]);
        return { swapped: false, message: this.#text('Partial'), reconcile: removed.status !== 'rejected' };
    }
}
if (!customElements.get('cart-line-variant')) {
    customElements.define('cart-line-variant', CartLineVariant);
}
